import React, { useState, useEffect } from 'react';
import { Modal, Badge, Spinner } from 'react-bootstrap';
import usersIcon from '../../assets/images/users_icon.png';
import search_icon from '../../assets/images/search_icon.png';
import eyeIcon from '../../assets/images/eyeIcon.png';
import brownfilter_icon from '../../assets/images/brownfilter_icon.png';
import { GetCustomers } from '../../Services/UserService';
import { GetAllOrder } from '../../Services/OrderService';
import api from '../../config/axios';
import endpoints from '../../config/endpoints';

export default function Customers() {
  const [customers, setCustomers] = useState([]);
  const [allSheetOrders, setAllSheetOrders] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  
  // Selected customer details modal state
  const [showModal, setShowModal] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerOrders, setCustomerOrders] = useState([]);
  const [isLoadingOrders, setIsLoadingOrders] = useState(false);

  const userRole = localStorage.getItem("userRole");
  const vendorId = userRole === "vendor" ? localStorage.getItem("userId") : null;

  // Helper to normalize phone number to last 10 digits
  const normPhone = (phoneStr) => {
    if (!phoneStr) return '';
    const digits = phoneStr.toString().replace(/[^0-9]/g, '');
    return digits.length >= 10 ? digits.slice(-10) : digits;
  };

  // Helper: map Google Sheet row to Order object
  const mapSheetOrderToOrder = (row) => {
    const grainAddons = row.addOnGrains ? row.addOnGrains.split('|').map(s => s.trim()).filter(Boolean) : [];
    const grainQtys = row.addOnGrainsQty ? row.addOnGrainsQty.split('|').map(s => s.trim()) : [];
    const pulseAddons = row.addOnPulses ? row.addOnPulses.split('|').map(s => s.trim()).filter(Boolean) : [];
    const pulseQtys = row.addOnPulsesQty ? row.addOnPulsesQty.split('|').map(s => s.trim()) : [];

    const subProducts = [
      ...grainAddons.map((g, i) => ({ name: g, weight: grainQtys[i] || '' })),
      ...pulseAddons.map((p, i) => ({ name: p, weight: pulseQtys[i] || '' }))
    ];

    const attributes = [];
    if (row.grading) {
      attributes.push({ name: 'Grinding Texture', value: row.grading });
    }
    if (row.packaging) {
      attributes.push({ name: 'Packaging', value: row.packaging });
    }

    const parsedTotal = Number((row.total || '').toString().replace(/[^0-9.]/g, '')) || 0;
    const parsedWeight = Number((row.quantity || '').toString().replace(/[^0-9.]/g, '')) || 0;

    let createdOnUtc = new Date().toISOString();
    if (row.date) {
      const s = row.date.toString().trim();
      const parts = s.split(' ');
      const dateStr = parts[0];
      const timeStr = parts[1] || '00:00:00';
      const dParts = dateStr.split(/[-/]/);

      if (dParts.length === 3) {
        let year, month, day;
        if (dParts[0].length === 4) {
          year = dParts[0];
          month = dParts[1].padStart(2, '0');
          day = dParts[2].padStart(2, '0');
        } else if (dParts[2].length === 4) {
          day = dParts[0].padStart(2, '0');
          month = dParts[1].padStart(2, '0');
          year = dParts[2];
        }
        if (year && month && day) {
          const parsed = new Date(`${year}-${month}-${day}T${timeStr}`);
          if (!isNaN(parsed.getTime())) {
            createdOnUtc = parsed.toISOString();
          }
        }
      }
    }

    return {
      orderId: row.orderId || 'N/A',
      userName: row.customerName || 'N/A',
      userPhone: row.phone || '',
      shippingAddress: row.address || '',
      createdOnUtc: createdOnUtc,
      deliveryDate: '',
      weight: parsedWeight,
      totalPrice: parsedTotal,
      paymentStatus: row.status || 'Pending',
      orderStatus: row.status || 'Pending',
      paymentMethod: 'WhatsApp',
      products: [
        {
          productName: row.product || 'N/A',
          price: parsedTotal,
          weight: parsedWeight,
          quantity: 1,
          attributes: {
            product: [{ name: row.product || 'N/A', weight: row.quantity || '' }],
            subProducts: subProducts,
            attributes: attributes
          }
        }
      ]
    };
  };

  const loadCustomers = async () => {
    try {
      setIsLoading(true);
      const params = { role: "Customer" };
      if (vendorId) {
        params.vendorId = vendorId;
      }

      // Fetch DB Customers and Google Sheet Orders concurrently
      const [dbRes, sheetRes] = await Promise.all([
        GetCustomers(params),
        api.get(`${endpoints.WhatsApp.GetSheetOrders}?pageSize=1000`)
      ]);

      const dbCustomers = dbRes?.status ? (dbRes.data || []) : [];
      const rawSheetOrders = sheetRes?.data?.status ? (sheetRes.data.data || []) : [];
      const mappedSheetOrders = rawSheetOrders.map(row => mapSheetOrderToOrder(row));
      setAllSheetOrders(mappedSheetOrders);

      // Create lookup set of normalized DB customer phone numbers
      const dbPhoneSet = new Set(
        dbCustomers
          .map(c => normPhone(c.phone))
          .filter(Boolean)
      );

      // Group sheet orders by normalized phone / customer name to find WhatsApp-only customers
      const sheetCustomersMap = new Map();

      mappedSheetOrders.forEach(order => {
        const pKey = normPhone(order.userPhone);
        const nameKey = (order.userName || '').trim().toLowerCase();
        const key = pKey || nameKey;
        if (!key) return;

        // If customer is not already in DB
        if (!dbPhoneSet.has(pKey)) {
          if (!sheetCustomersMap.has(key)) {
            sheetCustomersMap.set(key, {
              id: `sheet-${key}`,
              name: order.userName || 'WhatsApp Customer',
              email: '',
              phone: order.userPhone || '',
              source: 'WhatsApp',
              createdOnUtc: order.createdOnUtc
            });
          } else {
            // Update createdOnUtc to earliest date if older
            const existing = sheetCustomersMap.get(key);
            if (new Date(order.createdOnUtc) < new Date(existing.createdOnUtc)) {
              existing.createdOnUtc = order.createdOnUtc;
            }
          }
        }
      });

      const whatsappCustomers = Array.from(sheetCustomersMap.values());

      // Merge DB Customers + Sheet WhatsApp Customers
      const combined = [
        ...dbCustomers.map(c => ({ ...c, source: 'Website' })),
        ...whatsappCustomers
      ];

      setCustomers(combined);
    } catch (err) {
      console.error("Error loading customers:", err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadCustomers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleOpenDetails = async (customer) => {
    setSelectedCustomer(customer);
    setShowModal(true);
    setCustomerOrders([]);
    
    try {
      setIsLoadingOrders(true);
      const targetPhone = normPhone(customer.phone);
      const targetName = (customer.name || '').trim().toLowerCase();

      let websiteOrders = [];

      // If customer is a registered DB customer (numeric ID)
      if (typeof customer.id === 'number' || !customer.id.toString().startsWith('sheet-')) {
        const params = { userId: customer.id };
        if (vendorId) {
          params.vendorId = vendorId;
        }
        const res = await GetAllOrder(params);
        if (res?.status) {
          // Exclude zero-price DB WhatsApp stubs
          websiteOrders = (res.data.orders || []).filter(o => o.paymentMethod !== 'WhatsApp');
        }
      }

      // Find matching Google Sheet orders by phone number or name
      const matchingSheetOrders = allSheetOrders.filter(order => {
        const orderPhone = normPhone(order.userPhone);
        const orderName = (order.userName || '').trim().toLowerCase();
        if (targetPhone && orderPhone) {
          return orderPhone === targetPhone;
        }
        return orderName === targetName;
      });

      // Merge DB website orders + Sheet WhatsApp orders for this customer
      const mergedCustomerOrders = [...websiteOrders, ...matchingSheetOrders];
      mergedCustomerOrders.sort((a, b) => new Date(b.createdOnUtc) - new Date(a.createdOnUtc));

      setCustomerOrders(mergedCustomerOrders);
    } catch (err) {
      console.error("Error loading customer orders:", err);
    } finally {
      setIsLoadingOrders(false);
    }
  };

  // Filtered customers list based on search term
  const filteredCustomers = customers.filter(customer => {
    const term = searchTerm.toLowerCase();
    return (
      (customer.name && customer.name.toLowerCase().includes(term)) ||
      (customer.email && customer.email.toLowerCase().includes(term)) ||
      (customer.phone && customer.phone.includes(term))
    );
  });

  const getStatusBadge = (status) => {
    const s = status?.toLowerCase() || '';
    if (s === 'pending' || s === 'new' || s === 'cod') return <Badge bg="warning" className="text-dark">Pending</Badge>;
    if (s === 'processing' || s === 'in progress' || s === 'inprogress') return <Badge bg="primary">Processing</Badge>;
    if (s === 'outfordelivery' || s === 'out for delivery') return <Badge bg="info" className="text-dark">Out For Delivery</Badge>;
    if (s === 'completed' || s === 'delivered' || s === 'paid') return <Badge bg="success">Delivered</Badge>;
    return <Badge bg="secondary">{status || 'Pending'}</Badge>;
  };

  return (
    <div className="dashboard mt-5">
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mt-4 mb-5">
        <div>
          <h2>Customers List</h2>
          <p><small>{vendorId ? "Franchise Customers with lifetime order history (Website & WhatsApp)." : "All registered website & WhatsApp customers."}</small></p>
        </div>
      </div>

      <div className="order-list">
        {/* Search header */}
        <div className="order-header">
          <div className="tabs">
            <span className="fw-bold" style={{ color: "#5a3e2b", fontSize: "1.1rem" }}>
              Total Customers ({filteredCustomers.length})
            </span>
          </div>

          <div className="search-tools">
            <div className="search-wrapper">
              <input
                type="search"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search Customer"
              />
              <div className="icons">
                <img src={search_icon} alt="Search" className="search-icon" />
                <img src={brownfilter_icon} alt="Filter" className="filter-icon" />
              </div>
            </div>
          </div>
        </div>

        {/* Customer Table */}
        <div className="order-table-wrapper">
          <table>
            <thead>
              <tr>
                <th>#</th>
                <th>Customer Name</th>
                <th>Source</th>
                <th>Email</th>
                <th>Phone</th>
                <th className="text-center">Action</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan="6" className="text-center py-4">
                    <div className="spinner-border spinner-border-sm me-2" role="status"></div>
                    Loading customers...
                  </td>
                </tr>
              ) : filteredCustomers.length === 0 ? (
                <tr>
                  <td colSpan="6" className="text-center py-4 text-muted">
                    No customers found.
                  </td>
                </tr>
              ) : (
                filteredCustomers.map((customer, index) => {
                  const whatsAppLink = customer.phone ? `https://wa.me/${customer.phone.replace(/[^0-9]/g, '')}` : null;
                  return (
                    <tr key={customer.id}>
                      <td>{index + 1}</td>
                      <td>
                        <div className="customer-info" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <img src={usersIcon} alt="Profile" className="customer-img" />
                          <span className="fw-semibold">{customer.name || 'N/A'}</span>
                        </div>
                      </td>
                      <td>
                        {customer.source === 'WhatsApp' ? (
                          <span style={{ fontSize: '11px', padding: '3px 8px', borderRadius: '10px', backgroundColor: '#e8f5e9', color: '#2e7d32', fontWeight: '600' }}>
                            WhatsApp
                          </span>
                        ) : (
                          <span style={{ fontSize: '11px', padding: '3px 8px', borderRadius: '10px', backgroundColor: '#efebe9', color: '#5d4037', fontWeight: '600' }}>
                            Website
                          </span>
                        )}
                      </td>
                      <td>{customer.email || <span className="text-muted">N/A</span>}</td>
                      <td>
                        {customer.phone ? (
                          <a
                            href={whatsAppLink}
                            target="_blank"
                            rel="noreferrer"
                            style={{ color: '#25D366', fontWeight: '600', textDecoration: 'none' }}
                          >
                            {customer.phone}
                          </a>
                        ) : (
                          <span className="text-muted">N/A</span>
                        )}
                      </td>
                      <td className="text-center">
                        <img
                          src={eyeIcon}
                          alt="View Details"
                          title="View Details"
                          className="eye-icon"
                          style={{ cursor: "pointer", width: "20px", height: "20px" }}
                          onClick={() => handleOpenDetails(customer)}
                        />
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Customer Details & History Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg" centered scrollable className="self-order-modal">
        <Modal.Header closeButton className="border-0 pb-0 bg-white">
          <Modal.Title className="fw-bold" style={{ color: "#5a3e2b" }}>Customer Profile & Orders</Modal.Title>
        </Modal.Header>
        <Modal.Body className="px-4 py-3 bg-light">
          {selectedCustomer && (
            <div>
              {/* Profile Details Card */}
              <div className="bg-white rounded-3 p-3 mb-4 shadow-sm border border-white">
                <div className="d-flex align-items-center gap-3">
                  <div className="bg-light p-2 rounded-circle" style={{ border: "2px solid #a67b5b" }}>
                    <img src={usersIcon} alt="User" width="50" height="50" />
                  </div>
                  <div>
                    <h4 className="fw-bold m-0" style={{ color: "#5a3e2b" }}>
                      {selectedCustomer.name}
                      {selectedCustomer.source === 'WhatsApp' && (
                        <span className="ms-2 badge bg-success" style={{ fontSize: '11px' }}>WhatsApp</span>
                      )}
                    </h4>
                    {selectedCustomer.createdOnUtc && (
                      <div className="text-muted small">Registered / First Seen: {new Date(selectedCustomer.createdOnUtc).toLocaleDateString()}</div>
                    )}
                  </div>
                </div>
                <hr className="my-3" style={{ borderTop: "1px solid #e0d7d0" }} />
                <div className="row g-2">
                  <div className="col-sm-6">
                    <span className="fw-bold text-secondary text-uppercase small d-block">Email</span>
                    <span className="fw-medium text-dark">{selectedCustomer.email || "N/A"}</span>
                  </div>
                  <div className="col-sm-6">
                    <span className="fw-bold text-secondary text-uppercase small d-block">Phone</span>
                    {selectedCustomer.phone ? (
                      <a
                        href={`https://wa.me/${selectedCustomer.phone.replace(/[^0-9]/g, '')}`}
                        target="_blank"
                        rel="noreferrer"
                        className="fw-bold text-success text-decoration-none"
                      >
                        {selectedCustomer.phone}
                      </a>
                    ) : (
                      <span className="fw-medium text-dark">N/A</span>
                    )}
                  </div>
                </div>
              </div>

              {/* Lifetime Order History Title */}
              <h5 className="fw-bold mb-3 d-flex justify-content-between align-items-center" style={{ color: "#5a3e2b" }}>
                <span>Lifetime Order History</span>
                <Badge bg="secondary">{customerOrders.length} Orders</Badge>
              </h5>

              {/* Past Orders List */}
              {isLoadingOrders ? (
                <div className="text-center py-4 bg-white rounded-3 shadow-sm">
                  <Spinner animation="border" variant="secondary" size="sm" className="me-2" />
                  Loading past orders...
                </div>
              ) : customerOrders.length === 0 ? (
                <div className="text-center py-4 bg-white rounded-3 shadow-sm text-muted">
                  No past orders found for this customer.
                </div>
              ) : (
                <div className="d-flex flex-column gap-3">
                  {customerOrders.map((order, idx) => {
                    const googleMapsLink = order.shippingAddress && (order.shippingAddress.startsWith('http') || order.shippingAddress.includes('maps?q=')) 
                      ? order.shippingAddress 
                      : null;

                    return (
                      <div key={order.orderId || idx} className="bg-white rounded-3 p-3 shadow-sm border border-light">
                        <div className="d-flex justify-content-between align-items-start mb-2">
                          <div>
                            <div className="d-flex align-items-center gap-2">
                              <span className="fw-bold text-dark" style={{ fontSize: "1.05rem" }}>
                                Order #{order.orderId}
                              </span>
                              {order.paymentMethod === 'WhatsApp' && (
                                <span className="badge bg-success" style={{ fontSize: '10px' }}>WhatsApp</span>
                              )}
                            </div>
                            <div className="text-muted small">
                              Placed on: {new Date(order.createdOnUtc).toLocaleString()}
                            </div>
                            {order.shippingAddress && (
                              <div className="mt-1 small" style={{ color: '#555' }}>
                                {googleMapsLink ? (
                                  <a href={googleMapsLink} target="_blank" rel="noreferrer" className="text-primary text-decoration-none fw-bold">
                                    📍 View Location on Map
                                  </a>
                                ) : (
                                  <span>📍 Address: {order.shippingAddress}</span>
                                )}
                              </div>
                            )}
                          </div>
                          <div className="d-flex align-items-center gap-2">
                            {getStatusBadge(order.orderStatus)}
                            <span className="fw-bold text-dark ms-2" style={{ fontSize: "1.1rem" }}>
                              ₹{Number(order.totalPrice).toLocaleString('en-IN')}
                            </span>
                          </div>
                        </div>
                        
                        {/* Nested ordered items list */}
                        <div className="d-flex flex-column gap-2 mt-2 pt-2 border-top" style={{ borderTopStyle: "dashed" }}>
                          {order.products?.map((prod, pIdx) => {
                            const productName = prod.productName || 'N/A';
                            const wheatVariant = prod.attributes?.product?.[0]?.name;
                            const attrs = (prod.attributes?.attributes || []).map(a => a.value).filter(Boolean);
                            const addons = prod.attributes?.subProducts || [];
                            const totalWeight = prod.weight || prod.quantity || 0;

                            return (
                              <div key={prod.orderItemId || pIdx} className="d-flex justify-content-between align-items-center p-2.5 rounded-2" style={{ backgroundColor: "#fff4e6" }}>
                                <div>
                                  <div className="fw-bold text-dark" style={{ fontSize: "0.95rem" }}>
                                    {productName}
                                  </div>
                                  {wheatVariant && wheatVariant !== productName && (
                                    <div style={{ color: '#795548', fontSize: '12px' }}>
                                      Wheat: {wheatVariant}
                                    </div>
                                  )}
                                  <div className="d-flex flex-wrap gap-1 mt-1">
                                    {attrs.map((val, aIdx) => (
                                      <span key={aIdx} className="badge bg-white text-secondary border border-light" style={{ fontSize: "0.75rem" }}>
                                        {val}
                                      </span>
                                    ))}
                                    {addons.map((sub, sIdx) => (
                                      <span key={sIdx} className="badge bg-secondary text-white" style={{ fontSize: "0.75rem" }}>
                                        + {sub.name} ({sub.weight})
                                      </span>
                                    ))}
                                  </div>
                                </div>
                                <div className="text-end fw-bold text-dark">
                                  {totalWeight} Kg · ₹{Number(prod.price).toLocaleString('en-IN')}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </Modal.Body>
      </Modal>
    </div>
  );
}
